import { useState } from "react";
import { formatRelative } from "../../lib/format";
import { useMembersIndex } from "../team/useMembersIndex";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { useLastActivityQuery } from "./api";

// Only the human-logged types ever reach this component (the server denormalises
// nothing else into lastLoggedActivityType), but fall back gracefully.
const TYPE_ICONS: Record<string, string> = {
  note: "📝",
  call: "📞",
  meeting: "🗓",
  email: "✉",
  whatsapp: "💬",
};

const TYPE_LABELS: Record<string, string> = {
  note: "Note",
  call: "Call",
  meeting: "Meeting",
  email: "Email",
  whatsapp: "WhatsApp",
};

interface Props {
  leadId: string;
  type: string | null;
  at: string | null;
}

// Chip under the status pill showing the lead's latest logged activity. The
// type/time come pre-loaded with the list row (denormalised, no extra query);
// the richer detail (actor, outcome, notes) is fetched lazily only on hover.
export function LastActivityCell({ leadId, type, at }: Props) {
  const [hovered, setHovered] = useState(false);
  const { current } = useWorkspace();
  const { resolveName } = useMembersIndex();
  const { data, isLoading } = useLastActivityQuery(
    current?.slug ?? null,
    leadId,
    hovered
  );

  // No logged activity yet — render nothing (ignore, per spec).
  if (!type) return null;

  const detail = data?.activity ?? null;

  return (
    <div
      className="last-activity"
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onFocus={() => setHovered(true)}
    >
      <span className="last-activity-chip">
        <span className="last-activity-icon">{TYPE_ICONS[type] ?? "•"}</span>
        {TYPE_LABELS[type] ?? type}
      </span>
      <div className="last-activity-pop" role="tooltip">
        {detail ? (
          <>
            <div className="last-activity-pop-head">
              <span>{TYPE_LABELS[detail.type] ?? detail.type}</span>
              {detail.actorUserId ? (
                <span className="muted">by {resolveName(detail.actorUserId)}</span>
              ) : null}
              {detail.outcome ? (
                <span className="timeline-outcome">{detail.outcome}</span>
              ) : null}
            </div>
            <div className="muted" title={detail.occurredAt}>
              {formatRelative(detail.occurredAt)}
            </div>
            {detail.body ? (
              <div className="last-activity-pop-body">{detail.body}</div>
            ) : null}
          </>
        ) : (
          // Until the hover fetch resolves, show the time we already have.
          <span className="muted" title={at ?? ""}>
            {isLoading ? "Loading…" : at ? formatRelative(at) : "—"}
          </span>
        )}
      </div>
    </div>
  );
}
